import React, { useState } from 'react';
import {
  ArrowLeft,
  ArrowRight,
  TrendingUp,
  Target,
  Lightbulb,
  BarChart3,
  Globe,
  Handshake,
  DollarSign,
  Briefcase,
  Download,
  CalendarDays,
  Lock,
  Mail,
  User,
  Building2,
  Phone,
  ChevronRight,
  Shield,
  Layers,
  Zap,
} from 'lucide-react';
import { getApiBase } from '../lib/apiBase';

/* ── Section Data ─────────────────────────────────────── */

const sections = [
  { id: 'overview', label: 'Executive Overview' },
  { id: 'problem', label: 'Problem' },
  { id: 'solution', label: 'Solution' },
  { id: 'products', label: 'Products' },
  { id: 'traction', label: 'Traction' },
  { id: 'revenue', label: 'Revenue Model' },
  { id: 'market', label: 'Market Opportunity' },
  { id: 'partnerships', label: 'Partnerships' },
  { id: 'use-of-funds', label: 'Use of Funds' },
  { id: 'investment', label: 'Investment Opportunity' },
];

const products = [
  {
    name: 'Caribbean Payments Alliance',
    desc: 'Payment orchestration platform enabling businesses to accept and route payments across multiple banks and digital wallets.',
    url: 'https://caribbeanpaymentsalliance.com',
    tag: 'Payments',
  },
  {
    name: 'APC Estate Flow',
    desc: 'Real estate and financing platform connecting property listings, transactions, and payment solutions.',
    url: 'https://apcestateflow.com',
    tag: 'Real Estate',
  },
  {
    name: 'Bahamas Pay',
    desc: 'Financial services platform for business incorporation, licensing, and payment enablement in The Bahamas.',
    url: 'https://bahamaspay.com',
    tag: 'Financial Services',
  },
  {
    name: 'CloudSync Pro',
    desc: 'Automation and integration platform connecting systems, data, and workflows across digital infrastructure.',
    url: 'https://csyncpro.com/',
    tag: 'Automation',
  },
];

const tractionMetrics = [
  { value: '60+', label: 'Merchants Onboarded' },
  { value: '500+', label: 'Transactions Processed' },
  { value: '4', label: 'Live Platforms' },
  { value: '3+', label: 'Regional Markets' },
];

const fundAllocation = [
  { label: 'Product Development & Engineering', pct: 35 },
  { label: 'Market Expansion (US, LATAM)', pct: 25 },
  { label: 'Sales & Business Development', pct: 20 },
  { label: 'Compliance & Licensing', pct: 10 },
  { label: 'Operations & Infrastructure', pct: 10 },
];

const partnerships = [
  'Scotiabank',
  'Fiserv IPG',
  'NACHA',
  'SWIFT',
  'Visa',
  'SunCash / Sand Dollar',
  'Plug\'n Pay',
  'Microsoft',
  'Intuit / QuickBooks',
];

/* ── Reusable Components ──────────────────────────────── */

const SectionHeader = ({ icon: Icon, label, title, children }) => (
  <div className="mb-10">
    <div className="flex items-center gap-2 mb-4">
      <Icon className="w-4 h-4 text-[#C9A227]" />
      <span className="text-[#C9A227] text-[10px] tracking-[0.3em] uppercase font-medium">
        {label}
      </span>
    </div>
    <h2 className="text-2xl sm:text-3xl font-light text-black mb-4 leading-tight">
      {title}
    </h2>
    {children && <div className="w-16 h-[2px] bg-[#C9A227]" />}
  </div>
);

const StatCard = ({ value, label }) => (
  <div className="p-6 bg-gray-50 border border-gray-200 text-center">
    <div className="text-2xl md:text-3xl font-light text-[#1e3a5f] mb-1">{value}</div>
    <div className="text-gray-500 text-xs uppercase tracking-wider">{label}</div>
  </div>
);

/* ── Main Page ────────────────────────────────────────── */

const API = getApiBase();

const InvestorPortalPage = () => {
  const [showLogin, setShowLogin] = useState(false);
  const [showMeeting, setShowMeeting] = useState(false);
  const [meetingSent, setMeetingSent] = useState(false);
  const [meetingSubmitting, setMeetingSubmitting] = useState(false);
  const [meetingError, setMeetingError] = useState('');
  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  const [meetingForm, setMeetingForm] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    message: '',
  });

  const handleMeetingSubmit = async (e) => {
    e.preventDefault();
    setMeetingSubmitting(true);
    setMeetingError('');
    try {
      const res = await fetch(`${API}/api/forms/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          form_type: 'investor',
          name: meetingForm.name,
          email: meetingForm.email,
          phone: meetingForm.phone,
          company: meetingForm.company,
          message: meetingForm.message,
        }),
      });
      if (!res.ok) throw new Error(`Submit failed (${res.status})`);
      setMeetingSent(true);
    } catch (err) {
      setMeetingError(err.message || 'Submission failed. Please try again.');
    } finally {
      setMeetingSubmitting(false);
    }
  };

  const handleLoginSubmit = (e) => {
    e.preventDefault();
    alert('Investor login is being configured. Please contact info@sebasalliance.com for access.');
  };

  return (
    <div className="min-h-screen bg-white" data-testid="investor-portal-page">
      {/* ── Header ── */}
      <header className="bg-black py-4 px-4 md:px-8 sticky top-0 z-50">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <a
            href="/"
            className="flex items-center gap-2 text-white/70 hover:text-white text-sm transition-colors"
            data-testid="investor-back-home"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="tracking-wide hidden sm:inline">Back to Home</span>
          </a>
          <span className="text-white/40 text-[10px] tracking-[0.3em] uppercase">
            Investor Portal
          </span>
          <div className="flex items-center gap-3">
            <button
              onClick={() => { setShowLogin(true); setShowMeeting(false); }}
              className="flex items-center gap-1.5 px-4 py-2 border border-white/20 text-white/70 text-xs tracking-wider uppercase hover:border-[#C9A227] hover:text-[#C9A227] transition-colors"
              data-testid="investor-login-btn"
            >
              <Lock className="w-3 h-3" />
              <span className="hidden sm:inline">Login</span>
            </button>
            <img
              src="/logos/sag-logo.png"
              alt="SAG"
              className="h-7 w-auto"
            />
          </div>
        </div>
      </header>

      {/* ── Sidebar Nav (desktop) + Content ── */}
      <div className="max-w-6xl mx-auto px-4 md:px-8 py-10 lg:py-16 flex gap-12">
        {/* Sidebar */}
        <nav className="hidden lg:block w-56 flex-shrink-0 sticky top-24 self-start">
          <ul className="space-y-1">
            {sections.map((s) => (
              <li key={s.id}>
                <a
                  href={`#${s.id}`}
                  className="group flex items-center gap-2 py-2 text-sm text-gray-400 hover:text-[#1e3a5f] transition-colors"
                  data-testid={`nav-${s.id}`}
                >
                  <ChevronRight className="w-3 h-3 text-[#C9A227] opacity-0 group-hover:opacity-100 transition-opacity" />
                  {s.label}
                </a>
              </li>
            ))}
          </ul>

          <div className="mt-8 pt-6 border-t border-gray-100 space-y-3">
            <button
              onClick={() => { setShowMeeting(true); setShowLogin(false); }}
              className="w-full py-3 bg-[#C9A227] text-black text-xs tracking-wider uppercase font-medium hover:bg-[#b8911f] transition-colors text-center"
              data-testid="sidebar-meeting-btn"
            >
              Schedule Meeting
            </button>
            <button
              onClick={() => { setShowLogin(true); setShowMeeting(false); }}
              className="w-full py-3 bg-[#1e3a5f] text-white text-xs tracking-wider uppercase font-medium hover:bg-black transition-colors text-center"
              data-testid="sidebar-request-btn"
            >
              Request Access
            </button>
          </div>
        </nav>

        {/* Main Content */}
        <main className="flex-1 min-w-0">
          {/* ─── Executive Overview ─── */}
          <section id="overview" className="mb-20" data-testid="section-overview">
            <SectionHeader icon={Briefcase} label="Overview" title="Executive Overview" />
            <p className="text-gray-600 leading-relaxed mb-6">
              Sebastian Alliance Group (SAG) is a vertically integrated fintech and digital infrastructure company headquartered in Fort Lauderdale, FL with operations across the Caribbean, United States, and expanding into LATAM markets.
            </p>
            <p className="text-gray-600 leading-relaxed mb-8">
              Founded in 2010, SAG builds and operates an ecosystem of digital platforms powering payments, commerce, real estate transactions, and financial services for businesses and individuals across underserved markets.
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <StatCard value="15+" label="Platforms & Solutions" />
              <StatCard value="Global" label="Payment Integrations" />
              <StatCard value="3+" label="Regional Markets" />
              <StatCard value="2010" label="Founded" />
            </div>
          </section>

          {/* ─── Problem ─── */}
          <section id="problem" className="mb-20" data-testid="section-problem">
            <SectionHeader icon={Target} label="The Problem" title="A $40B+ Market Left Behind">
              <div className="w-16 h-[2px] bg-[#C9A227]" />
            </SectionHeader>
            <div className="space-y-4">
              {[
                { title: 'Fragmented Payment Infrastructure', desc: 'Caribbean and emerging-market businesses face disconnected banking systems, limited digital payment options, and high barriers to merchant account approval.' },
                { title: 'No Unified Platform', desc: 'Businesses are forced to manage multiple vendors for payments, e-commerce, compliance, and operations — creating inefficiency and cost overruns.' },
                { title: 'Financial Exclusion', desc: 'Freelancers, small vendors, and service providers in the Caribbean lack access to simple, affordable payment acceptance tools that are standard in developed markets.' },
              ].map((item, i) => (
                <div key={i} className="p-6 border border-gray-200 bg-gray-50">
                  <h3 className="text-sm font-medium text-black uppercase tracking-wider mb-2">{item.title}</h3>
                  <p className="text-gray-500 text-sm leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </section>

          {/* ─── Solution ─── */}
          <section id="solution" className="mb-20" data-testid="section-solution">
            <SectionHeader icon={Lightbulb} label="Our Solution" title="Integrated Digital Ecosystem">
              <div className="w-16 h-[2px] bg-[#C9A227]" />
            </SectionHeader>
            <p className="text-gray-600 leading-relaxed mb-8">
              SAG has built an integrated ecosystem of digital platforms that together deliver end-to-end infrastructure — from payment processing and e-commerce to real estate financing and business automation — all connected, all under one umbrella.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                { icon: Layers, title: 'Vertically Integrated', desc: 'Payments, commerce, real estate, and automation all connected through one ecosystem.' },
                { icon: Globe, title: 'Multi-Market', desc: 'Built for the Caribbean, scaled for the US and LATAM — serving underbanked and emerging markets.' },
                { icon: Zap, title: 'Rapid Deployment', desc: 'Merchants go live in 48 hours. QuickPay users in 5 minutes. No complex onboarding.' },
              ].map((item, i) => {
                const Icon = item.icon;
                return (
                  <div key={i} className="p-6 border border-gray-200">
                    <Icon className="w-6 h-6 text-[#C9A227] mb-4" />
                    <h3 className="text-sm font-medium text-black uppercase tracking-wider mb-2">{item.title}</h3>
                    <p className="text-gray-500 text-sm leading-relaxed">{item.desc}</p>
                  </div>
                );
              })}
            </div>
          </section>

          {/* ─── Products ─── */}
          <section id="products" className="mb-20" data-testid="section-products">
            <SectionHeader icon={Layers} label="Portfolio" title="Our Digital Platforms">
              <div className="w-16 h-[2px] bg-[#C9A227]" />
            </SectionHeader>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {products.map((p, i) => (
                <a
                  key={i}
                  href={p.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group p-6 border border-gray-200 bg-gray-50 hover:border-[#C9A227]/40 hover:shadow-md hover:-translate-y-1 transition-all duration-300"
                  data-testid={`product-card-${i}`}
                >
                  <span className="text-[10px] tracking-[0.2em] uppercase text-[#C9A227] font-medium block mb-2">{p.tag}</span>
                  <h3 className="text-lg font-medium text-black mb-2 group-hover:text-[#1e3a5f] transition-colors">{p.name}</h3>
                  <p className="text-gray-500 text-sm leading-relaxed mb-4">{p.desc}</p>
                  <span className="flex items-center gap-1 text-xs text-[#1e3a5f] uppercase tracking-wider group-hover:text-[#C9A227] transition-colors">
                    Visit Platform <ArrowRight className="w-3 h-3 group-hover:translate-x-1 transition-transform" />
                  </span>
                </a>
              ))}
            </div>
          </section>

          {/* ─── Traction ─── */}
          <section id="traction" className="mb-20" data-testid="section-traction">
            <SectionHeader icon={TrendingUp} label="Traction" title="Growth & Milestones">
              <div className="w-16 h-[2px] bg-[#C9A227]" />
            </SectionHeader>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              {tractionMetrics.map((m, i) => (
                <StatCard key={i} value={m.value} label={m.label} />
              ))}
            </div>
            <div className="p-6 bg-[#1e3a5f] text-white">
              <h3 className="text-sm font-medium uppercase tracking-wider mb-4 text-[#C9A227]">Key Milestones</h3>
              <ul className="space-y-3">
                {[
                  'First WooCommerce plugin with direct Scotiabank integration — live in 30+ countries',
                  'QuickPay platform aligned with Central Bank of The Bahamas Sand Dollar (CBDC) initiative',
                  'Strategic partnerships with Fiserv IPG, NACHA, SWIFT, and Plug\'n Pay',
                  'BBB A+ Accredited Business',
                  'Expanding to LATAM and US mainland markets',
                ].map((m, i) => (
                  <li key={i} className="flex items-start gap-3 text-white/70 text-sm">
                    <span className="w-1.5 h-1.5 bg-[#C9A227] rounded-full mt-1.5 flex-shrink-0" />
                    {m}
                  </li>
                ))}
              </ul>
            </div>
          </section>

          {/* ─── Revenue Model ─── */}
          <section id="revenue" className="mb-20" data-testid="section-revenue">
            <SectionHeader icon={DollarSign} label="Monetization" title="Revenue Model">
              <div className="w-16 h-[2px] bg-[#C9A227]" />
            </SectionHeader>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {[
                { title: 'Transaction Fees', desc: 'Percentage-based fees on every payment processed through CPA, QuickPay, and merchant solutions.' },
                { title: 'SaaS Subscriptions', desc: 'Monthly and annual licensing for CloudSync Pro, Turnkey E-Store, and WooCommerce plugins.' },
                { title: 'Platform Licensing', desc: 'White-label and enterprise licensing of payment infrastructure to banks and financial institutions.' },
                { title: 'Professional Services', desc: 'Custom development, integration consulting, and managed services for enterprise clients.' },
              ].map((item, i) => (
                <div key={i} className="p-6 border border-gray-200">
                  <h3 className="text-sm font-medium text-black uppercase tracking-wider mb-2">{item.title}</h3>
                  <p className="text-gray-500 text-sm leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </section>

          {/* ─── Market Opportunity ─── */}
          <section id="market" className="mb-20" data-testid="section-market">
            <SectionHeader icon={Globe} label="Market" title="Market Opportunity">
              <div className="w-16 h-[2px] bg-[#C9A227]" />
            </SectionHeader>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              {[
                { value: '$40B+', label: 'Caribbean Digital Economy', sub: 'Payments, commerce, and financial services' },
                { value: '44M+', label: 'Population Served', sub: 'Caribbean region alone' },
                { value: '<15%', label: 'Digital Payment Penetration', sub: 'Massive growth runway' },
              ].map((m, i) => (
                <div key={i} className="p-6 bg-[#1e3a5f] text-center">
                  <div className="text-2xl md:text-3xl font-light text-white mb-1">{m.value}</div>
                  <div className="text-[#C9A227] text-xs uppercase tracking-wider mb-1">{m.label}</div>
                  <div className="text-white/40 text-xs">{m.sub}</div>
                </div>
              ))}
            </div>
            <p className="text-gray-600 text-sm leading-relaxed">
              The Caribbean represents one of the last major underserved markets for digital financial infrastructure. With low digital payment penetration, high smartphone adoption, and increasing regulatory support for digital currencies (Sand Dollar CBDC), the timing is ideal for SAG's integrated approach.
            </p>
          </section>

          {/* ─── Partnerships ─── */}
          <section id="partnerships" className="mb-20" data-testid="section-partnerships">
            <SectionHeader icon={Handshake} label="Partners" title="Strategic Partnerships">
              <div className="w-16 h-[2px] bg-[#C9A227]" />
            </SectionHeader>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              {partnerships.map((p, i) => (
                <div key={i} className="p-4 border border-gray-200 bg-gray-50 text-center">
                  <span className="text-sm font-medium text-[#1e3a5f]">{p}</span>
                </div>
              ))}
            </div>
          </section>

          {/* ─── Use of Funds ─── */}
          <section id="use-of-funds" className="mb-20" data-testid="section-use-of-funds">
            <SectionHeader icon={BarChart3} label="Allocation" title="Use of Funds">
              <div className="w-16 h-[2px] bg-[#C9A227]" />
            </SectionHeader>
            <div className="space-y-4">
              {fundAllocation.map((item, i) => (
                <div key={i}>
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-sm text-gray-700">{item.label}</span>
                    <span className="text-sm font-medium text-[#1e3a5f]">{item.pct}%</span>
                  </div>
                  <div className="w-full h-2 bg-gray-100 overflow-hidden">
                    <div
                      className="h-full bg-[#1e3a5f]"
                      style={{ width: `${item.pct}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* ─── Investment Opportunity ─── */}
          <section id="investment" className="mb-10" data-testid="section-investment">
            <SectionHeader icon={Briefcase} label="Invest" title="Investment Opportunity">
              <div className="w-16 h-[2px] bg-[#C9A227]" />
            </SectionHeader>
            <div className="p-8 bg-[#1e3a5f] mb-8">
              <p className="text-white/70 leading-relaxed mb-8">
                SAG is raising capital to accelerate platform development, expand into the US and LATAM markets, strengthen banking partnerships, and scale merchant acquisition. We are seeking strategic investors who align with our vision of building the financial infrastructure backbone for the Caribbean and emerging markets.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-8">
                {[
                  { label: 'Stage', value: 'Growth' },
                  { label: 'Focus', value: 'Fintech Infrastructure' },
                  { label: 'Geography', value: 'Caribbean, US, LATAM' },
                ].map((item, i) => (
                  <div key={i} className="text-center">
                    <div className="text-xs text-[#C9A227] uppercase tracking-wider mb-1">{item.label}</div>
                    <div className="text-white text-lg font-light">{item.value}</div>
                  </div>
                ))}
              </div>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button
                  onClick={() => { setShowMeeting(true); setShowLogin(false); }}
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-[#C9A227] text-black text-xs tracking-[0.15em] uppercase font-medium hover:bg-white transition-colors"
                  data-testid="invest-meeting-btn"
                >
                  <CalendarDays className="w-4 h-4" />
                  Schedule Meeting
                </button>
                <button
                  onClick={() => { setShowLogin(true); setShowMeeting(false); }}
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 border border-white/30 text-white text-xs tracking-[0.15em] uppercase font-medium hover:border-[#C9A227] hover:text-[#C9A227] transition-colors"
                  data-testid="invest-access-btn"
                >
                  <Lock className="w-4 h-4" />
                  Request Full Access
                </button>
              </div>
            </div>

            {/* Pitch Deck Download */}
            <div className="p-6 border border-gray-200 bg-gray-50 flex flex-col sm:flex-row items-center justify-between gap-4">
              <div>
                <h3 className="text-sm font-medium text-black uppercase tracking-wider mb-1">Pitch Deck</h3>
                <p className="text-gray-500 text-xs">Download the SAG investor presentation (PDF)</p>
              </div>
              <a
                href="/SAG_Pitch_Deck.pdf"
                download
                className="inline-flex items-center gap-2 px-6 py-3 bg-black text-white text-xs tracking-wider uppercase hover:bg-[#1e3a5f] transition-colors"
                data-testid="download-pitch-deck"
              >
                <Download className="w-4 h-4" />
                Download PDF
              </a>
            </div>
          </section>

          {/* Mobile CTAs */}
          <div className="lg:hidden flex flex-col gap-3 mt-10">
            <button
              onClick={() => { setShowMeeting(true); setShowLogin(false); }}
              className="w-full py-4 bg-[#C9A227] text-black text-xs tracking-wider uppercase font-medium"
              data-testid="mobile-meeting-btn"
            >
              Schedule Meeting
            </button>
            <button
              onClick={() => { setShowLogin(true); setShowMeeting(false); }}
              className="w-full py-4 bg-[#1e3a5f] text-white text-xs tracking-wider uppercase font-medium"
              data-testid="mobile-access-btn"
            >
              Request Access
            </button>
          </div>
        </main>
      </div>

      {/* ── Modal Overlay ── */}
      {(showLogin || showMeeting) && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4"
          onClick={() => { setShowLogin(false); setShowMeeting(false); setMeetingSent(false); }}
        >
          <div
            className="bg-white w-full max-w-md p-8 relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => { setShowLogin(false); setShowMeeting(false); setMeetingSent(false); }}
              className="absolute top-4 right-4 text-gray-400 hover:text-black text-lg"
              data-testid="modal-close"
            >
              &times;
            </button>

            {/* Login Modal */}
            {showLogin && (
              <>
                <div className="flex items-center gap-2 mb-6">
                  <Lock className="w-4 h-4 text-[#C9A227]" />
                  <h3 className="text-sm font-medium text-black uppercase tracking-wider">Investor Login</h3>
                </div>
                <p className="text-gray-500 text-xs mb-6">
                  Access full investor materials including financials, projections, and legal documents.
                </p>
                <form onSubmit={handleLoginSubmit} className="space-y-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">Email</label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input
                        type="email"
                        required
                        value={loginForm.email}
                        onChange={(e) => setLoginForm(p => ({ ...p, email: e.target.value }))}
                        className="w-full pl-10 pr-4 py-3 border border-gray-200 text-sm focus:outline-none focus:border-[#C9A227]"
                        placeholder="investor@company.com"
                        data-testid="login-email"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">Password</label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input
                        type="password"
                        required
                        value={loginForm.password}
                        onChange={(e) => setLoginForm(p => ({ ...p, password: e.target.value }))}
                        className="w-full pl-10 pr-4 py-3 border border-gray-200 text-sm focus:outline-none focus:border-[#C9A227]"
                        placeholder="Enter password"
                        data-testid="login-password"
                      />
                    </div>
                  </div>
                  <button
                    type="submit"
                    className="w-full py-3 bg-[#1e3a5f] text-white text-xs tracking-wider uppercase font-medium hover:bg-black transition-colors"
                    data-testid="login-submit"
                  >
                    Sign In
                  </button>
                  <p className="text-center text-xs text-gray-400">
                    Don't have access?{' '}
                    <button
                      type="button"
                      onClick={() => { setShowLogin(false); setShowMeeting(true); }}
                      className="text-[#C9A227] hover:underline"
                    >
                      Request Access
                    </button>
                  </p>
                </form>
              </>
            )}

            {/* Meeting / Request Access Modal */}
            {showMeeting && !meetingSent && (
              <>
                <div className="flex items-center gap-2 mb-6">
                  <CalendarDays className="w-4 h-4 text-[#C9A227]" />
                  <h3 className="text-sm font-medium text-black uppercase tracking-wider">Schedule a Meeting</h3>
                </div>
                <p className="text-gray-500 text-xs mb-6">
                  Connect with our team to discuss investment opportunities and access full materials.
                </p>
                <form onSubmit={handleMeetingSubmit} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">Name *</label>
                      <input
                        type="text"
                        required
                        value={meetingForm.name}
                        onChange={(e) => setMeetingForm(p => ({ ...p, name: e.target.value }))}
                        className="w-full px-4 py-3 border border-gray-200 text-sm focus:outline-none focus:border-[#C9A227]"
                        data-testid="meeting-name"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">Company</label>
                      <input
                        type="text"
                        value={meetingForm.company}
                        onChange={(e) => setMeetingForm(p => ({ ...p, company: e.target.value }))}
                        className="w-full px-4 py-3 border border-gray-200 text-sm focus:outline-none focus:border-[#C9A227]"
                        data-testid="meeting-company"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">Email *</label>
                    <input
                      type="email"
                      required
                      value={meetingForm.email}
                      onChange={(e) => setMeetingForm(p => ({ ...p, email: e.target.value }))}
                      className="w-full px-4 py-3 border border-gray-200 text-sm focus:outline-none focus:border-[#C9A227]"
                      placeholder="investor@company.com"
                      data-testid="meeting-email"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">Phone</label>
                    <input
                      type="tel"
                      value={meetingForm.phone}
                      onChange={(e) => setMeetingForm(p => ({ ...p, phone: e.target.value }))}
                      className="w-full px-4 py-3 border border-gray-200 text-sm focus:outline-none focus:border-[#C9A227]"
                      data-testid="meeting-phone"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">Message</label>
                    <textarea
                      rows={3}
                      value={meetingForm.message}
                      onChange={(e) => setMeetingForm(p => ({ ...p, message: e.target.value }))}
                      className="w-full px-4 py-3 border border-gray-200 text-sm focus:outline-none focus:border-[#C9A227] resize-none"
                      placeholder="Tell us about your interest..."
                      data-testid="meeting-message"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={meetingSubmitting}
                    className="w-full py-3 bg-[#C9A227] text-black text-xs tracking-wider uppercase font-medium hover:bg-[#b8911f] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                    data-testid="meeting-submit"
                  >
                    {meetingSubmitting ? 'Submitting…' : 'Submit Request'}
                  </button>
                  {meetingError && (
                    <p className="text-center text-red-600 text-xs" data-testid="meeting-error">{meetingError}</p>
                  )}
                </form>
              </>
            )}

            {/* Success state */}
            {showMeeting && meetingSent && (
              <div className="text-center py-6">
                <div className="w-14 h-14 bg-[#C9A227]/10 flex items-center justify-center mx-auto mb-4">
                  <Shield className="w-7 h-7 text-[#C9A227]" />
                </div>
                <h3 className="text-lg font-medium text-black mb-2">Request Submitted</h3>
                <p className="text-gray-500 text-sm mb-4">
                  Our investor relations team will contact you within 24-48 hours.
                </p>
                <p className="text-xs text-gray-400">
                  For immediate assistance: <a href="mailto:info@sebasalliance.com" className="text-[#C9A227]">info@sebasalliance.com</a>
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Footer ── */}
      <footer className="bg-black py-8 px-4 text-center">
        <p className="text-white/30 text-xs tracking-wider mb-2">
          Sebastian Alliance Group &bull; Investor Relations
        </p>
        <p className="text-white/20 text-[10px] tracking-wider">
          This material is for informational purposes only and does not constitute an offer to sell securities.
        </p>
      </footer>
    </div>
  );
};

export default InvestorPortalPage;
